// ---------------------------------------------------------------------------
// NewSyntheticDatasetPage — Full-page shell for creating a synthetic dataset.
//
// Left rail: the generation form. Right pane: live preview of the run
// (polled via `SyntheticRunPreview`). Top bar exposes the Save action,
// which delegates back into the preview component.
// ---------------------------------------------------------------------------

import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Title2,
  Toast,
  ToastBody,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from '@fluentui/react-components';
import { Sparkle24Regular } from '@fluentui/react-icons';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { AppError } from '../../api/errors.js';
import { useHttpClient } from '../../api/HttpClientContext.js';
import { DatalakeAccessDenied } from '../../components/Datalake/DatalakeAccessDenied/DatalakeAccessDenied.js';
import {
  KG_FORM_DEFAULTS,
  KgGenerationForm,
  SyntheticRunPreview,
  isKgFormValid,
} from '../../components/SyntheticGen/index.js';
import type {
  KgGenerationFormValues,
  SyntheticRunPreviewHandle,
} from '../../components/SyntheticGen/index.js';
import { DataHubClientProvider } from '../../context/DataHubClientContext.js';
import { useDatalakeAccess } from '../../hooks/useDatalakeAccess/useDatalakeAccess.js';
import { useSyntheticJob } from '../../hooks/useSyntheticJob/useSyntheticJob.js';
import { WorkflowApiService } from '../../services/workflowApi.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  EntityType,
  OperationType,
  PanelType,
} from '../../telemetry/userbi.js';

import { useStyles } from './NewSyntheticDatasetPage.style.js';
import { toCreateRequest } from './toCreateRequest.js';

export function NewSyntheticDatasetPage() {
  return (
    <DataHubClientProvider>
      <NewSyntheticDatasetContent />
    </DataHubClientProvider>
  );
}

function NewSyntheticDatasetContent() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const httpClient = useHttpClient();
  const { trackOperation } = useUserBi();
  const workflowService = useMemo(
    () => new WorkflowApiService(httpClient),
    [httpClient],
  );

  const toasterId = useId('new-synthetic-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const [values, setValues] =
    useState<KgGenerationFormValues>(KG_FORM_DEFAULTS);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generation is billable and slow, so the entitlement that gates saving is
  // checked up front rather than at the end of the flow.
  const { isDenied } = useDatalakeAccess();

  const runId = searchParams.get('jobId');
  const valid = isKgFormValid(values);

  // Poll the current run so the Generate button can reflect live status
  // (running until the job reaches a terminal state).
  const { job, isTerminal } = useSyntheticJob(runId ?? undefined);
  const isRunning =
    isSubmitting || (runId !== null && (job === null || !isTerminal));

  // Top-bar Save wires through to the preview component via an imperative
  // handle; the preview owns the underlying flow (dialog + DataHub calls).
  const previewRef = useRef<SyntheticRunPreviewHandle>(null);
  const [canSave, setCanSave] = useState(false);

  const handleGenerate = useCallback(async () => {
    if (!valid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const { runId: newRunId } = await trackOperation(
        {
          panelType: PanelType.DatasetsPage,
          entityType: EntityType.SyntheticRun,
          operation: OperationType.Generate,
        },
        () => workflowService.createSyntheticRun(toCreateRequest(values)),
      );
      // Preserve form state; surface the run in the URL so it survives reload.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('jobId', newRunId);
          return next;
        },
        { replace: true },
      );
    } catch (err) {
      const message =
        err instanceof AppError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to start synthetic generation.';
      dispatchToast(
        <Toast>
          <ToastTitle>Generation request failed</ToastTitle>
          <ToastBody>{message}</ToastBody>
        </Toast>,
        { intent: 'error', timeout: 6000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    valid,
    isSubmitting,
    workflowService,
    values,
    trackOperation,
    setSearchParams,
    dispatchToast,
  ]);

  return (
    <div className={styles.root}>
      <Toaster toasterId={toasterId} position="top-end" />

      <div className={styles.topBar}>
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/datasets" className={styles.breadcrumbLink}>
            Datasets
          </Link>
          <span>/</span>
          <span className={styles.breadcrumbCurrent}>
            New Synthetic Dataset
          </span>
        </nav>
        <div className={styles.topBarActions}>
          <Button
            appearance="primary"
            disabled={!canSave || isDenied}
            onClick={() => previewRef.current?.openSaveDialog()}
          >
            Save dataset
          </Button>
          <Button
            appearance="secondary"
            onClick={() => {
              void navigate('/datasets');
            }}
          >
            Close
          </Button>
        </div>
      </div>

      {isDenied && (
        <MessageBar
          intent="error"
          layout="multiline"
          className={styles.accessDenied}
        >
          <MessageBarBody>
            <DatalakeAccessDenied action="save datasets" hideTitle />
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.panelsRow}>
        {/* Left: config */}
        <aside className={styles.leftPanel}>
          <div className={styles.leftHeader}>
            <span className={styles.methodPill}>Knowledge Graph</span>
          </div>

          <div className={styles.leftScroll}>
            <KgGenerationForm
              values={values}
              onChange={setValues}
              disabled={isRunning}
            />
          </div>

          <div className={styles.leftFooter}>
            <Button
              type="button"
              appearance="primary"
              disabled={!valid || isRunning || isDenied}
              onClick={() => {
                void handleGenerate();
              }}
            >
              {isRunning
                ? 'Generating\u2026'
                : runId
                  ? 'Re-generate'
                  : 'Generate'}
            </Button>
            {isRunning && <Spinner size="extra-small" />}
          </div>
        </aside>

        {/* Right: preview / poller */}
        <main className={styles.rightPanel}>
          <div className={styles.rightHeader}>
            <div className={styles.rightHeaderLeft}>
              <Title2>Preview</Title2>
              {runId && <span className={styles.footerHint}>Run {runId}</span>}
            </div>
          </div>

          <div className={styles.rightScroll}>
            {runId ? (
              <SyntheticRunPreview
                jobId={runId}
                controlsRef={previewRef}
                onCanSaveChange={setCanSave}
              />
            ) : (
              <div className={styles.emptyState}>
                <Sparkle24Regular className={styles.emptyIcon} />
                <span className={styles.emptyTitle}>No run yet</span>
                <span>
                  Configure the generation on the left and click{' '}
                  <strong>Generate</strong> to see results here.
                </span>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
